import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {pastelColors} from 'lib/Colors';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover, {ClickPopoverProps, ControlledPopoverProps} from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface PopoverColorPickerProps {
  className?: string;
  selectedColor: string | null;
  onSelectColor: (color: string) => void;
  floatingInteraction?: FloatingInteraction;
  asDropdown?: boolean;
  disabled?: boolean;
}

/**
 * Renders a color swatch with the selected color that can be clicked to select a new color.
 *
 * @param className CSS class to give to the color picker.
 * @param selectedColor Currently selected color.
 * @param onSelectColor Callback when a color is selected.
 * @param floatingInteraction Type of floating interaction for popover.
 * @param asDropdown Whether to render the toggle as a dropdown button instead of a swatch.
 * @param disabled Whether the color choice is changeable.
 */
export default function PopoverColorPicker({
  className,
  selectedColor,
  onSelectColor,
  floatingInteraction = FloatingInteraction.CONTROLLED,
  asDropdown = false,
  disabled = false,
}: PopoverColorPickerProps): JSX.Element {
  assert(asDropdown || selectedColor != null, 'Expected non-null color for swatch colorpicker');
  const [isOpen, setIsOpen] = useState(false);
  // Might want to make these props eventually.
  const palette = pastelColors;
  const itemsPerRow = 4;
  const renderContent = (): JSX.Element => {
    const groups = groupBy(palette, (item) => Math.floor(palette.indexOf(item) / itemsPerRow));
    const rows = [];
    for (const [rowNumber, colors] of Object.entries(groups)) {
      rows.push(
        <Row key={rowNumber} className={css(styles.swatchRow)}>
          {colors.map((color) => (
            <Col key={color} className={css(styles.swatchCol)}>
              <ColorSwatch
                fillColor={color}
                darkerBorder={color === selectedColor}
                onClick={() => {
                  onSelectColor(color);
                  setIsOpen(false);
                }}
                dataTestAttrs={{component: 'PopoverColorPicker', element: 'selector'}}
              />
            </Col>
          ))}
        </Row>
      );
    }
    return (
      <div className={css(styles.popover)}>
        <h3 className={css(styles.title)}>Select color</h3>
        <div className={css(styles.popoverContent)}>{rows}</div>
      </div>
    );
  };
  const controlledProps: Omit<ControlledPopoverProps, 'children'> = {
    renderContent: () => renderContent(),
    placement: FloatingPlacement.LEFT,
    interaction: FloatingInteraction.CONTROLLED,
    isOpen,
  };
  const clickProps: Omit<ClickPopoverProps, 'children'> = {
    renderContent: () => renderContent(),
    placement: FloatingPlacement.LEFT,
    interaction: FloatingInteraction.CLICK,
    isDisabled: disabled,
  };
  return (
    <Popover {...(floatingInteraction === FloatingInteraction.CONTROLLED ? controlledProps : clickProps)}>
      <div
        className={className}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
          }
        }}
        {...getTestProps({component: 'PopoverColorPicker', element: 'toggle'})}
      >
        {asDropdown ? (
          <div
            className={classnames(css(styles.asDropdownButton), 'btn-af-default selectDropdown-button', {
              'is-disabled': disabled,
            })}
          >
            {selectedColor == null ? (
              <span className={css(styles.dropdownText)}>Keep existing colors</span>
            ) : (
              <ColorSwatch fillColor={selectedColor} darkerBorder={false} small={true} />
            )}
            <BenchlingIcon className={css(styles.colorButtonCaret)} icon="chevron-down" />
          </div>
        ) : (
          <ColorSwatch fillColor={selectedColor!} darkerBorder={false} />
        )}
      </div>
    </Popover>
  );
}

const styles = StyleSheet.create({
  swatchRow: {
    margin: '0 0 5px 0',
  },
  swatchCol: {
    display: 'inline-block',
    paddingLeft: '0px',
    paddingRight: '0px',
    marginRight: '5px',
  },
  popover: {
    maxWidth: '276px',
  },
  popoverContent: {
    padding: '10px 10px',
  },
  title: {
    margin: '0',
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: '18px',
    backgroundColor: '#f7f7f7',
    borderBottom: '1px solid #ebebeb',
    borderRadius: '5px 5px 0 0',
  },
  asDropdownButton: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownText: {
    ...FONT_PROXIMA_13,
    textTransform: 'none',
  },
  colorButtonCaret: {
    color: GRAY_DARKER,
  },
});
